import { useState } from 'react';
import { Lock, FileText, Check, AlertCircle, Loader2, Shield, Clock, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { generatePartialPreviewHtml, formatDateFr } from '../utils/previewUtils';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI28q4NOcW936FcvOgjC04";

export default function LettreResiliationPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    adresse: '',
    organisme: '',
    motif: '',
    lieu: '',
    date_document: new Date().toISOString().split('T')[0]
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    return formData.nom && formData.prenom && formData.adresse && 
           formData.organisme && formData.lieu && formData.date_document;
  };
  
  const generatePreviewHtml = () => {
    const { nom, prenom, adresse, organisme, motif, lieu, date_document } = formData;
    
    const introText = `Je soussigné(e) ${prenom} ${nom.toUpperCase()}, demeurant à ${adresse.replace(/\n/g, ', ')}, vous informe par la présente de ma décision de résilier mon contrat/abonnement auprès de ${organisme}.`;
    const content = motif ? `Motif de la résiliation : ${motif}` : `Je vous prie de bien vouloir prendre en compte cette demande de résiliation dans les meilleurs délais et de me faire parvenir une confirmation écrite.`;
    
    return generatePartialPreviewHtml({
      nom,
      prenom,
      adresse,
      dateStr: formatDateFr(date_document),
      lieu,
      title: "LETTRE DE RÉSILIATION",
      content,
      introText
    });
  };
  
  const handleGeneratePreview = () => {
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setPreviewLoading(true);
    setError(null);
    
    setTimeout(() => {
      const html = generatePreviewHtml();
      setPreviewHtml(html);
      setCurrentStep(2);
      setPreviewLoading(false);
    }, 500);
  };
  
  const handlePayment = () => {
    setPaymentLoading(true);
    
    const paymentData = {
      documentId: 'lettre-resiliation',
      documentName: 'Lettre de résiliation',
      formData: formData,
      previewHtml: previewHtml,
      timestamp: Date.now()
    };
    localStorage.setItem('pdf_facile_payment', JSON.stringify(paymentData));
    
    window.location.href = STRIPE_PAYMENT_LINK;
  };
  
  return (
    <>
      <Helmet>
        <title>Lettre de résiliation PDF – Modèle prêt à l'emploi | PDF Facile</title>
        <meta name="description" content="Créez votre lettre de résiliation en ligne. Modèle générique valable pour assurance, abonnement, box internet, téléphone. Document conforme, aperçu gratuit, PDF à 4,99€." />
        <link rel="canonical" href="/lettre-resiliation-pdf" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        
        <main className="flex-1">
          <section className="bg-slate-50 py-8 md:py-12">
            <div className="container-app max-w-3xl">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4" data-testid="resiliation-h1">
                Lettre de résiliation à télécharger en PDF
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Générez une lettre de résiliation <strong>conforme et prête à l'emploi</strong>. 
                Ce modèle générique convient pour résilier une assurance, un abonnement internet, 
                un forfait téléphone ou tout autre service. Sans inscription, prêt en 2 minutes.
              </p>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>Modèle conforme</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>Prêt en 2 min</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Sans inscription</span>
                </div>
              </div>
            </div>
          </section>
          
          <section className="py-4 border-b border-slate-200">
            <div className="container-app max-w-3xl">
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'inactive'}`}>
                    {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Formulaire</span>
                </div>
                <div className={`step-line flex-1 max-w-[60px] ${currentStep > 1 ? 'completed' : ''}`} />
                <div className="flex items-center gap-2">
                  <div className={`step-dot ${currentStep >= 2 ? 'active' : 'inactive'}`}>
                    2
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">Aperçu & Téléchargement</span>
                </div>
              </div>
            </div>
          </section>
          
          <section className="section-spacing">
            <div className="container-app max-w-2xl">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              
              {currentStep === 1 && (
                <div data-testid="resiliation-form-step">
                  <h2 className="text-lg font-semibold mb-6">Vos informations</h2>
                  
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="nom" className="label-field">
                          Nom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="nom"
                          type="text"
                          placeholder="Dupont"
                          value={formData.nom}
                          onChange={(e) => handleInputChange('nom', e.target.value)}
                          className="input-field"
                          data-testid="field-nom"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="prenom" className="label-field">
                          Prénom <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="prenom"
                          type="text"
                          placeholder="Jean"
                          value={formData.prenom}
                          onChange={(e) => handleInputChange('prenom', e.target.value)}
                          className="input-field"
                          data-testid="field-prenom"
                        />
                      </div>
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="adresse" className="label-field">
                        Adresse <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="adresse"
                        placeholder="123 rue de la Paix, 75001 Paris"
                        value={formData.adresse}
                        onChange={(e) => handleInputChange('adresse', e.target.value)}
                        className="input-field min-h-[80px] py-3"
                        data-testid="field-adresse"
                      />
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="organisme" className="label-field">
                        Nom de l'organisme <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="organisme"
                        type="text"
                        placeholder="Orange, SFR, AXA, Netflix..."
                        value={formData.organisme}
                        onChange={(e) => handleInputChange('organisme', e.target.value)}
                        className="input-field"
                        data-testid="field-organisme"
                      />
                    </div>
                    
                    <div className="form-field">
                      <Label htmlFor="motif" className="label-field">
                        Motif de résiliation <span className="text-slate-400 text-xs">(optionnel)</span>
                      </Label>
                      <Textarea
                        id="motif"
                        placeholder="Déménagement, changement de situation, fin d'engagement..."
                        value={formData.motif}
                        onChange={(e) => handleInputChange('motif', e.target.value)}
                        className="input-field min-h-[80px] py-3"
                        data-testid="field-motif"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-field">
                        <Label htmlFor="lieu" className="label-field">
                          Lieu <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="lieu"
                          type="text"
                          placeholder="Paris"
                          value={formData.lieu}
                          onChange={(e) => handleInputChange('lieu', e.target.value)}
                          className="input-field"
                          data-testid="field-lieu"
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="date_document" className="label-field">
                          Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="date_document"
                          type="date"
                          value={formData.date_document}
                          onChange={(e) => handleInputChange('date_document', e.target.value)}
                          className="input-field"
                          data-testid="field-date"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Button
                      onClick={handleGeneratePreview}
                      disabled={!validateForm() || previewLoading}
                      className="btn-primary w-full"
                      data-testid="generate-preview-btn"
                    >
                      {previewLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5 mr-2" />
                          Générer l'aperçu gratuitement
                        </>
                      )}
                    </Button>
                    
                    {/* Réassurance sous le bouton */}
                    <div className="mt-6 text-xs text-slate-500 space-y-1">
                      <p>• Documents conformes aux usages administratifs français</p>
                      <p>• Aperçu partiel gratuit avant paiement</p>
                      <p>• Téléchargement immédiat après paiement</p>
                      <p>• Aucune inscription requise</p>
                    </div>
                  </div>
                </div>
              )}
              
              {currentStep === 2 && previewHtml && (
                <div data-testid="resiliation-preview-step">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold">Aperçu de votre lettre</h2>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-accent hover:underline"
                    >
                      Modifier
                    </button>
                  </div>
                  
                  <div 
                    className="preview-document mb-6"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                  
                  <div className="bg-slate-50 rounded-lg p-6 text-center">
                    <div className="payment-badge inline-flex mb-4">
                      <Lock className="w-4 h-4" />
                      <span>Paiement sécurisé</span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">
                      Votre lettre est prête
                    </h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      Le PDF sera identique à l'aperçu ci-dessus, au format A4.
                    </p>
                    
                    <Button
                      onClick={handlePayment}
                      disabled={paymentLoading}
                      className="btn-primary w-full"
                      data-testid="pay-button"
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          Télécharger le PDF – 4,99 €
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-4">
                      Paiement unique • Sans abonnement • Téléchargement immédiat
                    </p>
                    
                    <p className="text-xs text-slate-400 mt-3">
                      Paiement sécurisé via Stripe – transactions protégées par chiffrement SSL
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
          
          <section className="bg-slate-50 section-spacing">
            <div className="container-app max-w-3xl">
              <h2 className="text-xl font-semibold mb-4">
                Comment rédiger une lettre de résiliation ?
              </h2>
              <div className="prose prose-slate max-w-none text-muted-foreground">
                <p>
                  La lettre de résiliation est un courrier officiel permettant de mettre fin à un contrat 
                  ou un abonnement. Elle peut être utilisée pour résilier une assurance (auto, habitation, santé), 
                  un abonnement internet ou téléphonique, un forfait mobile, ou tout autre service souscrit.
                </p>
                <p>
                  Pour être efficace, une lettre de résiliation doit contenir : vos coordonnées complètes, 
                  le nom de l'organisme destinataire, la mention claire de votre demande de résiliation, 
                  et votre signature. Le motif de résiliation n'est pas toujours obligatoire, mais peut 
                  accélérer le traitement de votre demande.
                </p>
                <p>
                  <strong>Conseil :</strong> Envoyez votre lettre en recommandé avec accusé de réception 
                  pour conserver une preuve de votre demande. Respectez les délais de préavis indiqués 
                  dans vos conditions générales.
                </p>
                <p>
                  Notre modèle de lettre de résiliation est rédigé dans un ton neutre et professionnel, 
                  conforme aux usages administratifs français. L'utilisateur reste responsable de 
                  l'exactitude des informations fournies.
                </p>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
